'use client';
import { useMemo } from 'react';
import { useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query';

import {
  EventsListResponse,
  MarketsQueryParams,
  PricesHistoryQueryParams,
  SportsFutureCategoriesResponse,
  SportsSidebarResponse,
} from '@/types';
import { marketsApi } from '../api/markets.api';
import { sportsApi } from '../api/sports.api';

export const sportsKeys = {
  all: ['sports'] as const,
  sidebar: () => [...sportsKeys.all, 'sidebar'] as const,
  futures: (params?: MarketsQueryParams) => [...sportsKeys.all, 'futures', params] as const,
  futureCategories: () => [...sportsKeys.all, 'future-categories'] as const,
  props: (params?: MarketsQueryParams) => [...sportsKeys.all, 'props', params] as const,
  games: (params?: MarketsQueryParams) => [...sportsKeys.all, 'games', params] as const,
  pricesHistory: (params: { market: string; interval?: string; fidelity?: number }) =>
    [...sportsKeys.all, 'prices-history', params] as const,
  live: (params?: MarketsQueryParams) => [...sportsKeys.all, 'live', params] as const,
  startingSoon: (params?: MarketsQueryParams) =>
    [...sportsKeys.all, 'starting-soon', params] as const,
  previousMarkets: (params?: MarketsQueryParams) =>
    [...sportsKeys.all, 'previous-markets', params] as const,
  byId: (id: string) => [...sportsKeys.all, 'by-id', id] as const,
};

export const useSportsSidebarData = () => {
  return useQuery({
    queryKey: sportsKeys.sidebar(),
    queryFn: sportsApi.getSportsSidebarData,
    select: (response: SportsSidebarResponse) =>
      response?.data ?? { automated: [], nonAutomated: [], totalEvents: 0, timestamp: '' },
  });
};

export const useSportsFutureCategories = () => {
  return useQuery({
    queryKey: sportsKeys.futureCategories(),
    queryFn: sportsApi.getSportsFutureCategories,
    select: (response: SportsFutureCategoriesResponse) => response?.data ?? [],
  });
};

export const useSportsFutures = (params?: MarketsQueryParams) => {
  return useQuery({
    queryKey: sportsKeys.futures(params),
    queryFn: () => sportsApi.getSportsFutures(params),
    select: (response: EventsListResponse) => response?.data ?? [],
    enabled: !!params?.tag_id || !!params?.tag_slug,
  });
};

export const useSportsProps = (params?: MarketsQueryParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: sportsKeys.props(params),
    queryFn: () => marketsApi.getPaginated(params),
    select: (response) => response?.items ?? [],
    enabled: enabled,
  });
};

/**
 * useSportsGamesInfinite — infinite-scroll version of useSportsGames.
 * Each page uses the `pageParam` value as the API `offset`.
 */
export const useSportsGamesInfinite = (params?: MarketsQueryParams, enabled: boolean = true) => {
  return useInfiniteQuery({
    queryKey: sportsKeys.games(params),
    queryFn: ({ pageParam = 0 }) =>
      marketsApi.getPaginated({ ...params, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage) return undefined;
      return lastPage.page * lastPage.limit;
    },
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useSportsPricesHistory = (params: {
  market: string;
  interval?: string;
  fidelity?: number;
}) => {
  return useQuery({
    queryKey: sportsKeys.pricesHistory(params),
    queryFn: () => sportsApi.getSportsPricesHistory(params),
    enabled: !!params.market,
    refetchInterval: 2 * 60 * 1000,
    staleTime: 1 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    select: (response) => response?.data ?? [],
  });
};

export const useSportsPricesHistoryMultiple = (
  tokenIds: string[],
  options?: { interval?: string; fidelity?: number; refetchMs?: number },
) => {
  const { interval, fidelity, refetchMs } = options ?? {};
  // Live games move constantly, so a live view polls fast (caller passes a short
  // refetchMs); otherwise fall back to the 2-minute cadence. staleTime tracks
  // the poll so a remount / window-focus while live also refetches promptly
  // instead of showing data that's up to a minute old.
  const refetchInterval = refetchMs ?? 2 * 60 * 1000;
  const staleTime = Math.min(refetchInterval, 60 * 1000);
  return useQueries({
    // interval/fidelity are part of the query key so switching the time range
    // (or asking for finer live resolution) refetches instead of re-windowing
    // the same coarse dataset client-side.
    queries: tokenIds.map((tokenId) => ({
      queryKey: sportsKeys.pricesHistory({ market: tokenId, interval, fidelity }),
      queryFn: () => sportsApi.getSportsPricesHistory({ market: tokenId, interval, fidelity }),
      enabled: !!tokenId,
      refetchInterval,
      staleTime,
      gcTime: 5 * 60 * 1000,
      select: (response: any) => response?.data ?? [],
    })),
  });
};

export const useLiveEvents = (params?: MarketsQueryParams) => {
  const payload = {
    limit: 20,
    offset: 0,
    sport_type: true,
    live: true,
    order: 'endDate',
    ascending: true,
    tag_id: 100639,
    active: true,
    closed: false,
    search: params?.search,
  };
  return useQuery({
    queryKey: sportsKeys.live(payload),
    queryFn: () => marketsApi.getPaginated(payload),
    select: (response) => response?.items ?? [],
    refetchInterval: 2 * 60 * 1000,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * useLiveEventsInfinite — infinite-scroll version of useLiveEvents.
 * The plain query above hard-capped the Live board at the first 20 live
 * events across every league combined, so leagues past that cutoff (or with
 * few live games) silently never appeared. This pages through offset in the
 * same way useSportsGamesInfinite does for the Games tab.
 */
export const useLiveEventsInfinite = (params?: MarketsQueryParams) => {
  const basePayload = useMemo(
    () => ({
      limit: 20,
      sport_type: true,
      live: true,
      // Client requirement: the Live board should rank matches by volume,
      // highest first — was 'endDate' ascending (soonest-ending first).
      // `order`/`ascending` are forwarded as-is to Polymarket's Gamma /events
      // API (see gamma.service.js getEvents `allowedParams`), which supports
      // 'volume' as a sort field, so no backend change is needed.
      order: 'volume',
      ascending: false,
      tag_id: 100639,
      active: true,
      closed: false,
      search: params?.search,
    }),
    [params?.search],
  );

  return useInfiniteQuery({
    queryKey: sportsKeys.live(basePayload),
    queryFn: ({ pageParam = 0 }) =>
      marketsApi.getPaginated({ ...basePayload, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage) return undefined;
      return lastPage.page * lastPage.limit;
    },
    refetchInterval: 2 * 60 * 1000,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useStartingSoonEvents = (params?: MarketsQueryParams) => {
  const payload = useMemo(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    return {
      limit: 20,
      offset: 0,
      sport_type: true,
      order: 'endDate',
      ascending: true,
      tag_id: 100639,
      active: true,
      closed: false,
      end_date_min: now.toISOString(),
      live: false,
      search: params?.search,
    };
  }, [params?.search]);
  return useQuery({
    queryKey: sportsKeys.startingSoon(payload),
    queryFn: () => marketsApi.getPaginated(payload),
    select: (response) => response?.items ?? [],
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * useStartingSoonEventsInfinite — infinite-scroll version of useStartingSoonEvents,
 * same reasoning as useLiveEventsInfinite: the fixed limit:20 silently dropped
 * anything beyond the first page.
 */
export const useStartingSoonEventsInfinite = (params?: MarketsQueryParams) => {
  const basePayload = useMemo(() => {
    const now = new Date();
    now.setSeconds(0, 0);
    return {
      limit: 20,
      sport_type: true,
      order: 'endDate',
      ascending: true,
      tag_id: 100639,
      active: true,
      closed: false,
      end_date_min: now.toISOString(),
      live: false,
      search: params?.search,
    };
  }, [params?.search]);

  return useInfiniteQuery({
    queryKey: sportsKeys.startingSoon(basePayload),
    queryFn: ({ pageParam = 0 }) =>
      marketsApi.getPaginated({ ...basePayload, offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (!lastPage.hasNextPage) return undefined;
      return lastPage.page * lastPage.limit;
    },
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const usePreviousMarkets = (params?: MarketsQueryParams) => {
  const payload = useMemo(() => {
    const previousDate = new Date();
    previousDate.setSeconds(0, 0);
    previousDate.setDate(previousDate.getDate() - 1);
    return {
      limit: 20,
      offset: 0,
      sport_type: true,
      order: 'endDate',
      ascending: false,
      tag_id: 100639,
      active: true,
      closed: true,
      end_date_max: previousDate.toISOString(),
      series_id: params?.series_id,
      search: params?.search,
    };
  }, [params?.series_id, params?.search]);
  return useQuery({
    queryKey: sportsKeys.previousMarkets(payload),
    queryFn: () => marketsApi.getPaginated(payload),
    select: (response) => response?.items ?? [],
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
};

export const useSportsById = (id: string) => {
  return useQuery({
    queryKey: sportsKeys.byId(id),
    queryFn: () => marketsApi.getById(id),
    enabled: !!id,
    staleTime: 5 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: (query) => {
      const data = query.state.data as any;
      return data?.isLive ? 15 * 1000 : false;
    },
  });
};
